<template>
  <ExportModal
    :job-id="jobId"
    :title="title"
    :is-data-loaded="isDataLoaded"
    @export-to="onExportTo"
  >
    <template #copy-options>
      <div class="d-flex">
        <div class="my-auto">
          Copy All Sections
        </div>

        <v-btn
          class="my-auto ml-auto"
          variant="flat"
          color="primary"
          rounded="lg"
          height="32"
          :disabled="lastCopied === 'all'"
          @click="onCopyAllFieldsClick"
        >
          Copy All
        </v-btn>
      </div>

      <div class="d-flex">
        <div class="my-auto">
          <div>
            Copy Title
          </div>
        </div>

        <v-btn
          class="my-auto ml-auto"
          variant="outlined"
          color="primary"
          rounded="lg"
          height="32"
          :disabled="lastCopied === 'title'"
          @click="copyTitle"
        >
          Copy Title
        </v-btn>
      </div>

      <div
        v-for="field in fieldsWithFooterAndHeaderJoined"
        :key="field.id"
        class="d-flex"
      >
        <div class="my-auto">
          <div>
            Copy {{ field.label }}
          </div>
        </div>

        <v-btn
          class="my-auto ml-auto"
          variant="outlined"
          color="primary"
          rounded="lg"
          height="32"
          :disabled="lastCopied === field.id"
          @click="copyField(field.id, field.label)"
        >
          Copy
        </v-btn>
      </div>
    </template>
  </ExportModal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import ExportModal from '@/components/editor/ExportModal.vue';
import { useEditor } from '@/stores/editor/editor';
import { useDocumentEditor } from '@/stores/document/editor';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';
import { shouldHideIfEmpty } from '@/helpers/constants/hideEmptyFields';

const props = defineProps({
  jobId: { type: [String, Number], default: '' },
  title: { type: String, default: '' },
  isDataLoaded: { type: Boolean, default: false },
});

const emits = defineEmits(['tracking-event']);

const editorStore = useEditor();
const documentEditorStore = useDocumentEditor();
const { copyFieldToClipboard, copyAllFieldsToClipboard, copyToClipboard } = editorStore;
const { fields } = storeToRefs(documentEditorStore);

const lastCopied = ref('');

const fieldsWithFooterAndHeaderJoined = computed(() => joinFooterAndHeaderFields(fields.value)
  .filter((field) => !shouldHideIfEmpty({
    type: field.type,
    sections: field.sections,
    isTemplate: false,
  }))
  .filter((field) => field.html.length > 20)); // no copy button for empty field

const copyTitle = () => {
  copyToClipboard({ content: props.title });
  emits('tracking-event', { action: 'copyTitle', args: { job: props } });
  lastCopied.value = 'title';
};

const copyField = (id, field) => {
  copyFieldToClipboard(id);
  emits('tracking-event', { action: 'copyContent', args: { job: props, props: { field } } });
  lastCopied.value = id;
};

const onCopyAllFieldsClick = () => {
  copyAllFieldsToClipboard(props.title);
  emits('tracking-event', { action: 'copyAllFields', args: { job: props } });
  lastCopied.value = 'all';
};

const shareKeyParam = computed(() => {
  const { shareKey } = window.sessionStorage;
  return shareKey ? `&key=${shareKey}` : '';
});

const onExportTo = (param, mixpanelVar) => {
  emits('tracking-event', {
    action: 'editorLinkClick',
    args: { label: `Export: ${mixpanelVar}` },
  });

  window.open(`/recruit/download?id=${props.jobId}&format=${param}${shareKeyParam.value}`);
};
</script>
